import type { ButtonHTMLAttributes, ReactNode } from 'react'

import styles from './Button.module.css'

/**
 * v2 Button variants (Figma «Button», 35:108):
 * - `primary`   solid accent, black ink — the one action that moves money or completes the flow.
 * - `secondary` surface-1 pill with a strong hairline — parallel actions (Withdraw, Transfer, Save QR).
 * - `ghost`     accent text, no container — low-emphasis links (See all, Review, Cancel in a row).
 * - `danger`    solid down, black ink — destructive and irreversible.
 * `outline` and `quiet` are the pre-v2 names, kept so every existing call site still works:
 * `outline` renders as `secondary`, `quiet` as `ghost`.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline' | 'quiet'

/** L 48 for bottom actions, M 40 for inline pairs, S 32 inside cards. Every size keeps a 44 px hit area. */
export type ButtonSize = 'lg' | 'md' | 'sm'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * `primary` is the accent fill. Keep one visible per screen — that restraint
   * is what keeps the accent meaningful.
   */
  variant?: ButtonVariant
  size?: ButtonSize
  /** Stretch to the container width (bottom actions, sheet actions). */
  full?: boolean
  /** Shows a spinner in place of the icon, disables the button and sets aria-busy. */
  loading?: boolean
  /** Leading icon (lucide; 20 for L/M, 16 for S). */
  icon?: ReactNode
  children?: ReactNode
}

const VARIANT_CLASS: Record<ButtonVariant, string | undefined> = {
  primary: styles.primary,
  secondary: styles.secondary,
  outline: styles.secondary,
  ghost: styles.ghost,
  quiet: styles.ghost,
  danger: styles.danger,
}

const SIZE_CLASS: Record<ButtonSize, string | undefined> = {
  lg: styles.lg,
  md: styles.md,
  sm: styles.sm,
}

/** The one button family of the app: a pill in four variants and three sizes. */
export function Button({
  variant = 'secondary',
  size = 'lg',
  full = false,
  loading = false,
  icon,
  children,
  disabled,
  className,
  type = 'button',
  ...rest
}: ButtonProps): JSX.Element {
  const classes = [
    styles.button,
    VARIANT_CLASS[variant] ?? styles.secondary,
    SIZE_CLASS[size] ?? styles.lg,
    full ? styles.full : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className={styles.spinner} aria-hidden="true" />
      ) : icon ? (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {children}
    </button>
  )
}
